import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FileText, Users, BarChart3, Settings,
  LogOut, Menu, X, Sparkles, ChevronDown, Bell, Search, UserPlus, Shield, Megaphone, MessageSquare
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [adminUser, setAdminUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Load admin user from localStorage
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      setAdminUser(JSON.parse(userData));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    toast('Logged out successfully');
    navigate('/admin/login');
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: LayoutDashboard,
      roles: ['super_admin', 'programme_manager', 'moderator', 'content_editor']
    },
    {
      name: 'Scenarios',
      href: '/admin/scenarios',
      icon: FileText,
      roles: ['super_admin', 'programme_manager', 'content_editor']
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      roles: ['super_admin', 'programme_manager', 'moderator']
    },
    {
      name: 'Announcements',
      href: '/admin/announcements',
      icon: Megaphone,
      roles: ['super_admin', 'programme_manager']
    },
    {
      name: 'Feedback',
      href: '/admin/feedback',
      icon: MessageSquare,
      roles: ['super_admin', 'programme_manager', 'moderator']
    },
    {
      name: 'Notifications',
      href: '/admin/notifications',
      icon: Bell,
      roles: ['super_admin', 'programme_manager']
    },
    {
      name: 'Moderation',
      href: '/admin/moderation',
      icon: Shield,
      roles: ['super_admin', 'programme_manager', 'moderator']
    },
    {
      name: 'Invitations',
      href: '/admin/invitations',
      icon: UserPlus,
      roles: ['super_admin']
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: BarChart3,
      roles: ['super_admin', 'programme_manager']
    },
    {
      name: 'Settings',
      href: '/admin/settings',
      icon: Settings,
      roles: ['super_admin']
    }
  ];

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item =>
    item.roles.includes(adminUser?.admin_role)
  );

  const isActive = (path) => location.pathname === path;

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'bg-purple-100 text-purple-800',
      programme_manager: 'bg-blue-100 text-blue-800',
      moderator: 'bg-green-100 text-green-800',
      content_editor: 'bg-amber-100 text-amber-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleLabel = (role) => {
    const labels = {
      super_admin: 'Super Admin',
      programme_manager: 'Programme Manager',
      moderator: 'Moderator',
      content_editor: 'Content Editor'
    };
    return labels[role] || role;
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar - Desktop */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 280 : 80 }}
        className="hidden lg:flex flex-col bg-white border-r border-gray-200 overflow-hidden"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
          <AnimatePresence mode="wait">
            {sidebarOpen ? (
              <motion.div
                key="full"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-ssdp-teal to-ssdp-purple rounded-xl flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900">Pathways</h1>
                  <p className="text-xs text-gray-500">Admin Portal</p>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="icon"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="w-10 h-10 bg-gradient-to-br from-ssdp-teal to-ssdp-purple rounded-xl flex items-center justify-center"
              >
                <Sparkles className="w-5 h-5 text-white" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {filteredNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                isActive(item.href)
                  ? 'bg-indigo-50 text-ssdp-teal'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <AnimatePresence>
                {sidebarOpen && (
                  <motion.span
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="font-medium"
                  >
                    {item.name}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
          ))}
        </nav>

        {/* User Profile */}
        {adminUser && (
          <div className="p-4 border-t border-gray-200">
            <Link
              to="/admin/profile"
              className={`flex items-center gap-3 ${sidebarOpen ? 'p-3' : 'justify-center p-2'} bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer`}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-ssdp-teal to-ssdp-purple rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                {adminUser.username?.[0]?.toUpperCase()}
              </div>
              <AnimatePresence>
                {sidebarOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex-1 min-w-0"
                  >
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {adminUser.username}
                    </p>
                    <p className={`text-xs px-2 py-0.5 rounded-full inline-block ${getRoleBadgeColor(adminUser.admin_role)}`}>
                      {getRoleLabel(adminUser.admin_role)}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </Link>
          </div>
        )}

        {/* Collapse Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="hidden lg:block p-4 border-t border-gray-200 hover:bg-gray-50 transition-colors"
        >
          <Menu className="w-5 h-5 text-gray-600" />
        </button>
      </motion.aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-280 bg-white z-50 flex flex-col"
            >
              {/* Mobile sidebar content (same as desktop) */}
              <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-ssdp-teal to-ssdp-purple rounded-xl flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold text-gray-900">Pathways</h1>
                    <p className="text-xs text-gray-500">Admin Portal</p>
                  </div>
                </div>
                <button onClick={() => setMobileMenuOpen(false)}>
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>

              <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
                {filteredNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive(item.href)
                        ? 'bg-indigo-50 text-ssdp-teal'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                ))}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden"
          >
            <Menu className="w-6 h-6 text-gray-600" />
          </button>

          <div className="hidden lg:block text-lg font-semibold text-gray-900">
            {filteredNavigation.find(item => isActive(item.href))?.name || 'Admin Portal'}
          </div>

          <div className="flex items-center gap-4">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="text-gray-600 hover:text-gray-900"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline ml-2">Logout</span>
            </Button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
